import { Text, View } from 'react-native';
import {
  formatRestTime,
  formatWeight,
  recordedEffortLabel,
  roundTo,
  type EffortScale,
} from '@heraion/shared';

import { weightUnitLabels } from '../weightUnitLabels';
import { formatShortDate } from './formatDate';
import type { HistorySetRow } from './historySetRows';
import type { HistoryPanelStyles } from './HistoryPanel.styles';

type T = (k: string, opts?: Record<string, unknown>) => string;

// One performed set: when, what, and what it cost. Split out of HistoryPanel
// with LG-59, when the panel grew its three sections and this line started
// being rendered from three places instead of one.

export function HistorySetLine({
  row,
  locale,
  unit,
  effortScale,
  styles,
  t,
}: {
  row: HistorySetRow;
  locale: string;
  unit: 'kg' | 'lb';
  effortScale: EffortScale;
  styles: HistoryPanelStyles;
  t: T;
}) {
  const effort = recordedEffortLabel(
    { rir: row.rir, rpe: row.rpe, fail: row.fail },
    effortScale,
  );
  return (
    <View style={styles.setLine}>
      <Text style={styles.rowDate}>
        {formatShortDate(new Date(row.atMs).toISOString(), locale)}
      </Text>
      <Text style={styles.setChip} numberOfLines={1}>
        {setRowText(row, unit, locale, t)}
      </Text>
      {/* Absent effort prints NOTHING — an untagged set and an easy one are
          different facts, and a dash would flatten them. */}
      {effort ? <Text style={styles.effort}>{effort}</Text> : null}
    </View>
  );
}

// THE one way a performed set is worded, for all three sections. Reads the
// normalised row, so it does not care which door the set arrived through.
export function setRowText(
  row: HistorySetRow,
  unit: 'kg' | 'lb',
  locale: string,
  t: T,
): string {
  const weight =
    row.weightKg !== null && row.weightKg > 0
      ? formatWeight(row.weightKg, unit, locale, weightUnitLabels(t))
      : null;

  if (row.kind === 'bodyweight') {
    const base = t('mobile.logger.historyBodyweight', {
      defaultValue: '{{reps}} reps BW',
      reps: row.reps ?? 0,
    });
    return weight ? `${base} · ${weight}` : base;
  }

  // Cardio, holds and loaded carries lead with time or distance; reps have
  // nothing to say there.
  if (row.kind === 'cardio' || row.kind === 'timed' || row.kind === 'loaded-carry') {
    const parts: string[] = [];
    if (row.durationSec !== null) parts.push(formatRestTime(row.durationSec));
    if (row.distanceM !== null && row.distanceM > 0)
      parts.push(
        // DS-35: threshold on the meter value rounded to the precision the
        // "m" branch below would render it at, so 999.996 (→ "1000 m") flips
        // to km instead of printing a value that already reads as 1 km.
        roundTo(row.distanceM, 0) >= 1000
          ? `${(row.distanceM / 1000).toFixed(2)} km`
          : `${Math.round(row.distanceM)} m`,
      );
    if (weight) parts.push(weight);
    if (row.reps !== null && parts.length === 0) parts.push(`${row.reps}`);
    return parts.join(' · ');
  }

  if (row.reps === null) return weight ?? '—';
  return weight ? `${row.reps} × ${weight}` : `${row.reps}`;
}
